/* eslint-disable @atlassian/i18n/no-literal-string-in-jsx */
import { Box, Text } from '@atlaskit/primitives';
import { type DatabaseField, DatabaseFieldType } from '@databases/types/database';
import React, { type FunctionComponent } from 'react';

import AttachmentFieldSettings from './AttachmentFieldSettings.tsx';
import EntryBacklinkFieldSettings from './EntryBacklinkFieldSettings.tsx';
import EntryDetailsFieldSettings from './EntryDetailsFieldSettings.tsx';
import EntryLinkFieldSettings from './EntryLinkFieldSettings.tsx';
import HyperlinkFieldSettings from './HyperlinkFieldSettings.tsx';
import JiraDetailsFieldSettings from './JiraDetailsFieldSettings.tsx';
import JiraLinkFieldSettings from './JiraLinkFieldSettings.tsx';
import PageLinkFieldSettings from './PageLinkFieldSettings.tsx';
import PageStatusFieldSettings from './PageStatusFieldSettings.tsx';
import SelectFieldSettings from './SelectFieldSettings.tsx';
import UserFieldSettings from './user/UserFieldSettings.tsx';

const unreachable = (fieldType: never): never => {
  throw new Error(`Unsupported field settings menu type: ${fieldType}`);
};

const FieldSettingsPlaceholder: FunctionComponent<{
  fieldType: DatabaseFieldType;
}> = ({ fieldType }) => {
  return (
    <Box paddingBlock="space.100">
      <Text>
        {fieldType}
        {
          // temporary placeholder
        } field settings placeholder
      </Text>
    </Box>
  );
};

export const getFieldSettingsOptionsByType = (
  contentId: string,
  field: DatabaseField<DatabaseFieldType>
): React.ReactElement | null => {
  switch (field.type) {
    case DatabaseFieldType.ATTACHMENT:
      return (
        <Box paddingBlockEnd="space.025">
          <AttachmentFieldSettings
            contentId={contentId}
            field={field as DatabaseField<DatabaseFieldType.ATTACHMENT>}
          />
        </Box>
      );

    case DatabaseFieldType.BACKLINK:
      return (
        <Box paddingBlockEnd="space.025">
          <EntryBacklinkFieldSettings
            contentId={contentId}
            field={field as DatabaseField<DatabaseFieldType.BACKLINK>}
          />
        </Box>
      );

    case DatabaseFieldType.ENTRY_DETAILS:
      return (
        <Box paddingBlockEnd="space.025">
          <EntryDetailsFieldSettings
            contentId={contentId}
            field={field as DatabaseField<DatabaseFieldType.ENTRY_DETAILS>}
          />
        </Box>
      );

    case DatabaseFieldType.ENTRY_LINK:
      return (
        <Box paddingBlockEnd="space.050">
          <EntryLinkFieldSettings
            contentId={contentId}
            field={field as DatabaseField<DatabaseFieldType.ENTRY_LINK>}
          />
        </Box>
      );

    case DatabaseFieldType.HYPERLINK:
      return (
        <Box paddingBlockEnd="space.050">
          <HyperlinkFieldSettings
            contentId={contentId}
            field={field as DatabaseField<DatabaseFieldType.HYPERLINK>}
          />
        </Box>
      );

    case DatabaseFieldType.JIRA_ISSUE:
      return (
        <Box paddingBlockEnd="space.050">
          <JiraLinkFieldSettings
            contentId={contentId}
            field={field as DatabaseField<DatabaseFieldType.JIRA_ISSUE>}
          />
        </Box>
      );

    case DatabaseFieldType.JIRA_ISSUE_DETAILS:
      return (
        <Box paddingBlockEnd="space.025">
          <JiraDetailsFieldSettings
            contentId={contentId}
            field={field as DatabaseField<DatabaseFieldType.JIRA_ISSUE_DETAILS>}
          />
        </Box>
      );

    case DatabaseFieldType.NUMBER:
      return <FieldSettingsPlaceholder fieldType={field.type} />;

    case DatabaseFieldType.PAGE_DETAILS:
      return <FieldSettingsPlaceholder fieldType={field.type} />;

    case DatabaseFieldType.PAGE_LABELS:
      return <FieldSettingsPlaceholder fieldType={field.type} />;

    case DatabaseFieldType.PAGE_LINK:
      return (
        <Box paddingBlockEnd="space.025">
          <PageLinkFieldSettings
            contentId={contentId}
            field={field as DatabaseField<DatabaseFieldType.PAGE_LINK>}
          />
        </Box>
      );

    case DatabaseFieldType.PAGE_STATUS:
      return (
        <Box paddingBlockEnd="space.050">
          <PageStatusFieldSettings
            contentId={contentId}
            field={field as DatabaseField<DatabaseFieldType.PAGE_STATUS>}
          />
        </Box>
      );

    case DatabaseFieldType.SELECT:
      return (
        <SelectFieldSettings
          contentId={contentId}
          field={field as DatabaseField<DatabaseFieldType.SELECT>}
        />
      );

    case DatabaseFieldType.USER:
      return (
        <Box paddingBlockEnd="space.050">
          <UserFieldSettings
            contentId={contentId}
            field={field as DatabaseField<DatabaseFieldType.USER>}
          />
        </Box>
      );

    case DatabaseFieldType.TEXT:
    case DatabaseFieldType.DATE:
    case DatabaseFieldType.AI_SUMMARY:
    case DatabaseFieldType.CHECKBOX:
      return null;

    default:
      return unreachable(field.type);
  }
};
